/**
 * Sanitize form data by removing sensitive fields
 */
function sanitizeFormData(data: any): any {
  if (!data || typeof data !== 'object') return data;
  
  const sensitive = ['password', 'token', 'secret', 'key', 'api_key', 'auth'];
  const sanitized = { ...data };
  
  Object.keys(sanitized).forEach(key => {
    if (sensitive.some(s => key.toLowerCase().includes(s))) {
      sanitized[key] = '[REDACTED]';
    }
  });
  
  return sanitized;
}

/**
 * Lazily get Sentry — only loads the module when actually called
 */
const getSentry = () => import('@sentry/react');

/**
 * Log form validation errors
 */
export function logFormValidationError(
  formName: string,
  errors: any,
  formData?: any
) {
  getSentry().then(Sentry => {
    Sentry.addBreadcrumb({
      category: 'form',
      message: `Validation failed: ${formName}`,
      level: 'warning',
      data: {
        errors: JSON.stringify(errors),
      },
    });
  }).catch(() => {});
}

/**
 * Log form submission errors to Sentry with context
 */
export function logFormSubmissionError(
  formName: string,
  error: unknown,
  context: {
    operation?: 'create' | 'update' | 'delete';
    entityId?: string;
    formData?: any;
    additionalContext?: Record<string, any>;
  }
) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  
  getSentry().then(Sentry => {
    Sentry.withScope((scope) => {
      scope.setTag('error_category', 'submission');
      scope.setTag('form_name', formName);
      if (context.operation) {
        scope.setTag('operation', context.operation);
      }
      
      scope.setContext('Form Submission', {
        formName,
        operation: context.operation,
        entityId: context.entityId,
        formData: sanitizeFormData(context.formData),
        timestamp: new Date().toISOString(),
        ...context.additionalContext,
      });
      
      Sentry.addBreadcrumb({
        category: 'form',
        message: `Form submission failed: ${formName}`,
        level: 'error',
        data: {
          operation: context.operation,
          error: errorMessage,
        },
      });
      
      Sentry.captureException(error instanceof Error ? error : new Error(errorMessage));
    });
  }).catch(() => console.error('Form submission error:', errorMessage));
}

/**
 * Log form render errors to Sentry
 */
export function logFormRenderError(
  formName: string,
  error: Error,
  componentStack?: string
) {
  getSentry().then(Sentry => {
    Sentry.withScope((scope) => {
      scope.setTag('error_category', 'render');
      scope.setTag('form_name', formName);
      
      scope.setContext('Form Render Error', {
        formName,
        componentStack,
        timestamp: new Date().toISOString(),
      });
      
      Sentry.captureException(error);
    });
  }).catch(() => console.error('Form render error:', error));
}

/**
 * Add breadcrumb for form interactions
 */
export function addFormBreadcrumb(
  formName: string,
  action: string,
  data?: Record<string, any>
) {
  getSentry().then(Sentry => {
    Sentry.addBreadcrumb({
      category: 'form.interaction',
      message: `${formName}: ${action}`,
      level: 'info',
      data: sanitizeFormData(data),
    });
  }).catch(() => {});
}
